'use client';

import React from 'react';
import type { TriggerEvidence } from 'src/api/redux/profileConfiguration/types';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import { formatSignalType } from 'src/components/Profiles/shared/utils/signalGrouping';

interface EvidenceRowProps {
  signal: TriggerEvidence;
  isLast: boolean;
  groupLabel?: string;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const formatThreshold = (signal: TriggerEvidence) => {
  if (!signal.operator || signal.threshold == null) return null;
  const op = signal.operator.replace(/_/g, ' ');
  if (signal.signal_type === 'open_payments') {
    return `${capitalize(op)} ${formatCurrency(signal.threshold)}`;
  }
  const val = signal.threshold.toLocaleString();
  const unit = signal.metric === 'percentage' ? '%' : ` ${signal.metric ?? ''}`;
  return `${capitalize(op)} ${val}${unit}`;
};

const EvidenceRow: React.FC<EvidenceRowProps> = ({
  signal,
  isLast,
  groupLabel,
}) => {
  const isFreshFaces = signal.signal_type === 'fresh_faces';
  const resolvedLabel = signal.company_name ?? signal.payer_type ?? formatSignalType(signal.signal_type);
  const isSpecialty = signal.signal_type === 'specialty';

  // When the label duplicates the group header, promote something more useful:
  // - specialty signals → use expected value
  // - other signals → use threshold
  const isRedundant = groupLabel === resolvedLabel;
  const threshold = formatThreshold(signal);
  const primaryLabel = isFreshFaces
    ? signal.strategy_label ?? `Last ${signal.lookback_months ?? 3} months`
    : isRedundant
      ? isSpecialty && signal.expected
        ? signal.expected
        : threshold ?? resolvedLabel
      : resolvedLabel;
  const showThresholdSub = !isFreshFaces && !isRedundant && !isSpecialty && threshold;
  const showExpectedSub = !isFreshFaces && isSpecialty && !isRedundant;

  return (
    <div
      className={`flex items-start justify-between gap-4 px-4 py-3 ${
        !isLast ? 'border-b border-neutral-100' : ''
      }`}
      data-testid={`evidence-row-${signal.signal_type}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${
            signal.matched ? 'bg-green-500' : 'bg-red-400'
          }`}
          aria-label={signal.matched ? 'matched' : 'not matched'}
        />
        <div className="flex flex-col gap-0.5 min-w-0">
          <div
            className="font-medium text-sm text-neutral-900"
            data-testid={`evidence-label-${signal.signal_type}`}
          >
            {primaryLabel}
          </div>
          {isFreshFaces && signal.strategy_description && (
            <div className="text-xs text-neutral-400">
              {signal.strategy_description}
            </div>
          )}
          {showThresholdSub && (
            <div className="text-xs text-neutral-400">{threshold}</div>
          )}
          {showExpectedSub && (
            <div className="text-xs text-neutral-400">
              expected: {signal.expected}
            </div>
          )}
          {(signal.min_date || signal.max_date) && (
            <div className="text-xs text-neutral-400">
              {signal.min_date && signal.max_date
                ? `${signal.min_date} – ${signal.max_date}`
                : signal.min_date ?? signal.max_date}
            </div>
          )}
        </div>
      </div>
      <div
        className={`text-sm font-semibold whitespace-nowrap shrink-0 ${
          signal.matched ? 'text-neutral-900' : 'text-red-500'
        }`}
        data-testid={`evidence-value-${signal.signal_type}`}
      >
        {isFreshFaces
          ? signal.matched ? 'Yes' : 'No'
          : isSpecialty
            ? signal.actual || 'N/A'
            : signal.signal_type === 'open_payments'
              ? formatCurrency(signal.actual_value ?? 0)
              : signal.metric === 'percentage'
                ? `${Math.round(signal.actual_value ?? 0)}%`
                : `${(signal.actual_value ?? 0).toLocaleString()} ${signal.metric ?? ''}`}
      </div>
    </div>
  );
};

export default EvidenceRow;
